import {
  DatePicker,
  Input,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { fetchMyOperatorCalls } from "../../apis/call_intelligence.api";
import { RecordingPlayer } from "./customer_intelligence/shared";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const OUTCOME_COLOR: Record<string, string> = {
  received: "green",
  missed: "red",
};

const TEAMS = [
  "MD managers",
  "MD Presales",
  "MD Customer Success",
  "Mdepot Design Consultants",
];

const fmtDuration = (s?: number) => {
  if (s == null) return "-";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const MyOperatorTab = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [count, setCount] = useState(0);

  const [search, setSearch] = useState("");
  const [team, setTeam] = useState<string>();
  const [outcome, setOutcome] = useState<string>();
  const [dates, setDates] = useState<any>([dayjs().subtract(7, "day"), dayjs()]);

  const load = (p = page, size = pageSize) => {
    setLoading(true);
    const params: any = { page: p, page_size: size, search };
    if (team) params.team = team;
    if (outcome) params.agent_outcome = outcome;
    if (dates?.[0]) params.start_date = dates[0].startOf("day").toISOString();
    if (dates?.[1]) params.end_date = dates[1].endOf("day").toISOString();
    fetchMyOperatorCalls(params).then((res) => {
      setData(res?.results || []);
      setCount(res?.count || 0);
      setLoading(false);
    });
  };

  useEffect(() => {
    setPage(1);
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, outcome, dates]);

  const columns: ColumnsType<any> = [
    {
      title: "Date / Time",
      key: "start_time",
      width: 160,
      render: (r) =>
        r.start_time ? dayjs(r.start_time).format("DD MMM YY, HH:mm") : "-",
    },
    {
      title: "Customer",
      key: "customer",
      render: (r) => r.customer_number_raw || r.customer_number || "-",
    },
    { title: "Agent", dataIndex: "agent_name", key: "agent_name", render: (v) => v || "-" },
    { title: "Team", dataIndex: "team", key: "team", width: 170 },
    { title: "Dept", dataIndex: "department_name", key: "department_name", width: 150, render: (v) => v || "-" },
    {
      title: "Outcome",
      dataIndex: "agent_outcome",
      key: "agent_outcome",
      width: 100,
      render: (o) =>
        o ? <Tag color={OUTCOME_COLOR[o] || "default"}>{o}</Tag> : <Text type="secondary">—</Text>,
    },
    { title: "Duration", key: "duration_sec", width: 90, render: (r) => fmtDuration(r.duration_sec) },
    {
      title: "Recording",
      key: "recording",
      width: 300,
      render: (r) =>
        r.recording_url ? (
          <RecordingPlayer moLogId={r.id} fallbackUrl={r.recording_url} />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Customer / agent number or name"
          allowClear
          style={{ width: 280 }}
          onSearch={() => {
            setPage(1);
            load(1, pageSize);
          }}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <Select
          placeholder="Team"
          allowClear
          style={{ width: 220 }}
          value={team}
          onChange={setTeam}
          options={TEAMS.map((t) => ({ value: t, label: t }))}
        />
        <Select
          placeholder="Outcome"
          allowClear
          style={{ width: 140 }}
          value={outcome}
          onChange={setOutcome}
          options={[
            { value: "received", label: "Received" },
            { value: "missed", label: "Missed" },
          ]}
        />
        <RangePicker value={dates} onChange={setDates} />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        size="middle"
        scroll={{ x: 1250 }}
      />

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={count}
          showSizeChanger
          showTotal={(t) => `${t} calls`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
            load(p, size);
          }}
        />
      </div>
    </div>
  );
};

export default MyOperatorTab;
